/**
 * Window-side bridge to the service worker: trigger/observe sync, query
 * status, wipe on sign-out. All functions are safe to call in environments
 * without a service worker (tests, unsupported browsers) — they no-op.
 *
 * The message contract itself lives in src/sw/protocol.ts (types only,
 * shared with the SW bundle).
 */

import type {
  CacheInvalidationMessage,
  MinimumClientVersionMessage,
  SyncStatusMessage,
  SyncStatusPayload,
  UpdateReadyMessage,
  VersionInfo,
} from '@/sw/protocol';

export type {
  CacheInvalidationMessage,
  MinimumClientVersionMessage,
  SyncStageKey,
  SyncStageStatus,
  SyncState,
  SyncStatusMessage,
  UpdateReadyMessage,
  VersionInfo,
} from '@/sw/protocol';

export type SyncStatus = SyncStatusPayload;

function hasType(data: unknown, type: string): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === type
  );
}

export function isSyncStatusMessage(data: unknown): data is SyncStatusMessage {
  return hasType(data, 'SYNC_STATUS');
}

export function isCacheInvalidationMessage(
  data: unknown,
): data is CacheInvalidationMessage {
  return hasType(data, 'CACHE_INVALIDATION');
}

export function isUpdateReadyMessage(
  data: unknown,
): data is UpdateReadyMessage {
  return hasType(data, 'UPDATE_READY');
}

export function isMinimumClientVersionMessage(
  data: unknown,
): data is MinimumClientVersionMessage {
  return hasType(data, 'PREFERRED_MINIMUM_CLIENT_VERSION');
}

function swContainer(): ServiceWorkerContainer | undefined {
  return 'serviceWorker' in navigator ? navigator.serviceWorker : undefined;
}

/**
 * The SW to message: the controller, or — right after install, before
 * clients.claim() attaches it — the active worker of the registration.
 * Without the fallback, messages sent in that window are silently dropped
 * (first sync after install never fired; uninstall could skip the wipe).
 */
async function swTarget(): Promise<ServiceWorker | undefined> {
  const container = swContainer();
  if (!container) {
    return undefined;
  }
  if (container.controller) {
    return container.controller;
  }
  const registration = await container.getRegistration();
  return registration?.active ?? undefined;
}

/** Subscribe to SW broadcasts. Returns an unsubscribe function. */
export function onSwMessage(handler: (data: unknown) => void): () => void {
  const container = swContainer();
  if (!container) {
    return () => {};
  }
  const listener = (event: MessageEvent) => handler(event.data);
  container.addEventListener('message', listener);
  return () => container.removeEventListener('message', listener);
}

/** Fire-and-forget sync trigger; progress arrives as SYNC_STATUS broadcasts. */
export function requestSync(): void {
  void swTarget().then((target) =>
    target?.postMessage({ type: 'SYNC_REQUEST' }),
  );
}

async function requestReply<T>(message: { type: string }, timeoutMs = 3000) {
  const target = await swTarget();
  return new Promise<T | undefined>((resolve) => {
    if (!target) {
      resolve(undefined);
      return;
    }
    const channel = new MessageChannel();
    const timeout = setTimeout(() => resolve(undefined), timeoutMs);
    channel.port1.onmessage = (event) => {
      clearTimeout(timeout);
      resolve(event.data as T);
    };
    target.postMessage(message, [channel.port2]);
  });
}

export function getSyncStatus(): Promise<SyncStatus | undefined> {
  return requestReply<SyncStatus>({ type: 'GET_SYNC_STATUS' });
}

/**
 * Wipe all offline data (entities, queues, attachment cache). Call on
 * sign-out — after warning the user when pendingUploads/pendingEvents are
 * non-zero (pending offline changes are lost).
 */
export function signOutWipe(): Promise<unknown> {
  return requestReply({ type: 'SIGN_OUT' }, 10_000);
}

/**
 * Debug passthrough (console-only, like serv.queryCache): the SW stops
 * intercepting fetches entirely (as if not installed) while staying
 * registered. State lives in the SW's meta store, so it survives SW
 * restarts and page reloads until switched back or uninstalled.
 */
export async function setSwPassthrough(enabled: boolean): Promise<boolean> {
  const reply = await requestReply<{ ok?: boolean }>({
    type: 'SET_PASSTHROUGH',
    enabled,
  } as { type: string });
  return reply?.ok === true;
}

/**
 * Version + changelog of the WAITING worker (the pending update), queried
 * over a MessageChannel — the waiting bundle carries the new release notes;
 * this window's own constants describe the old build. Undefined when there
 * is no waiting worker or it doesn't reply in time.
 */
export async function getWaitingVersionInfo(): Promise<
  VersionInfo | undefined
> {
  const container = swContainer();
  if (!container) {
    return undefined;
  }
  const registration = await container.getRegistration();
  const waiting = registration?.waiting;
  if (!waiting) {
    return undefined;
  }
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timeout = setTimeout(() => resolve(undefined), 3000);
    channel.port1.onmessage = (event) => {
      clearTimeout(timeout);
      resolve(event.data as VersionInfo);
    };
    waiting.postMessage({ type: 'GET_VERSION' }, [channel.port2]);
  });
}
